import { useState } from "react";
import { toast } from "react-hot-toast";
import { useSupabaseCallback, useSupabaseLoader } from "../../../server/supabase/hooks";

import type { UUID } from "../../../utils/uuid";

const NONE: readonly string[] = [];

/**
 * The caller's favourite programs and folders, in the order they were starred, and a toggle that
 * stars or unstars one. program_favourite holds the favourites of each user in the account, so the
 * reads and the deletes go through my_program_favourite, which has only the caller's. A star goes
 * into program_favourite, whose profile_id is the caller's by default. The toggle shows the change
 * at once, and puts it back with a toast if it fails.
 */
export const useProgramFavourites = (): {
  favourites: readonly string[];
  toggle: (programId: UUID) => Promise<void>;
} => {
  const { data } = useSupabaseLoader(async ({ supabase }) => {
    const { data: rows, error } = await supabase
      .from("my_program_favourite")
      .select("program_id")
      .order("created_at");
    if (error) throw error;
    return rows.map(({ program_id }) => program_id).filter((id) => id !== null);
  }, []);
  const [changed, setChanged] = useState<readonly string[] | null>(null);
  const favourites = changed ?? data ?? NONE;

  const save = useSupabaseCallback(async ({ supabase }, program_id: UUID, favourite: boolean) => {
    const { error } = favourite
      ? await supabase.from("program_favourite").insert({ program_id })
      : await supabase.from("my_program_favourite").delete().eq("program_id", program_id);
    if (error) throw error;
  }, []);

  const toggle = async (programId: UUID): Promise<void> => {
    const favourite = !favourites.includes(programId);
    setChanged(
      favourite ? [...favourites, programId] : favourites.filter((id) => id !== programId),
    );
    try {
      await save(programId, favourite);
    } catch {
      setChanged(favourites);
      toast.error("The favourite could not be saved.");
    }
  };

  return { favourites, toggle };
};
